import React, { useEffect, useState } from "react";

import {
  Key,
  Lock,
  Mail,
  Pencil,
  Plus,
  Trash2,
  Unlock,
  Users,
} from "lucide-react";

import AddUserModal from "./AddUserModal";
import ResetPasswordModal from "./ResetPasswordModal";
import ToggleUserStatusModal from "./ToggleUserStatusModal";
import Button from "../../Components/Button";
import SuccessNotification from "../../Components/SuccessNotification";
import DeleteModal from "../../Container/DeleteModal";
import { useAppSelector, useAppDispatch } from "../../Store/Hooks";
import {
  deleteAdminUser,
  updateAdminUser,
  resetUserPassword,
  createAdminUser,
  resendWelcomeEmail,
  fetchAllUsers,
  clearCreateUserState,
  clearUpdateUserState,
  clearDeleteUserState,
  clearResetPasswordState,
  clearResendWelcomeEmailState,
} from "../../Store/Reducers/AdminSlice";
import { fetchAdminRoles } from "../../Store/Reducers/MasterSlice";
import {
  ACCOUNT_TEAM_ROLE_OPTIONS,
  ACCOUNT_TEAM_ROLE_FILTER,
  normalizeRoleName,
  ROLE_DISPLAY_NAMES,
  ROLES,
  isAccountTeamRole,
} from "../../Utils/Constants/RoleConstants";
import { formatDateUS } from "../../Utils/Helper/Helper";
import AdminTableContainer from "../AdminTableContainer";
import SendInvitationModal from "../VendorAdminContainer/VendorUserManagementContainer/SendInvitationModal";

import "./style.scss";

interface User {
  user_id: string;
  user: string;
  email: string;
  role: string;
  status: string;
  last_login_at: string;
  last_contacted_at: string;
  organization: string;
  tenant_schema: string;
}

interface AdminUserManagementContainerProps {
  users: User[];
  organizations: {
    label: string;
    value: string;
    tenant_schema_name?: string;
  }[];
  onSendEmail?: (id: string) => void;
  loading?: boolean;
}

const AdminUserManagementContainer: React.FC<
  AdminUserManagementContainerProps
> = ({ users, organizations, onSendEmail, loading = false }) => {
  const dispatch = useAppDispatch();
  const {
    deleteUserSuccess,
    deleteUserLoading,
    deleteUserError,
    updateUserSuccess,
    updateUserLoading,
    updateUserError,
    resetPasswordSuccess,
    resetPasswordLoading,
    resetPasswordError,
    createUserSuccess,
    createUserLoading,
    createUserError,
    resendWelcomeEmailLoading,
    resendWelcomeEmailSuccess,
    resendWelcomeEmailError,
  } = useAppSelector((state: any) => state.admin);
  const { roles } = useAppSelector((state: any) => state.master);
  const [searchValue, setSearchValue] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [toggleStatusModalOpen, setToggleStatusModalOpen] = useState(false);
  const [sendInvitationModalOpen, setSendInvitationModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    type: "success" | "error";
    message: string;
    details: string;
  }>({ show: false, type: "success", message: "", details: "" });

  useEffect(() => {
    if (organizations.length > 0) {
      setSelectedOrg(organizations[0].value);
    }
  }, [organizations]);

  useEffect(() => {
    dispatch(fetchAdminRoles() as any);
  }, [dispatch]);

  useEffect(() => {
    if (deleteUserSuccess || deleteUserError) {
      setTimeout(() => dispatch(clearDeleteUserState()), 3000);
    }
  }, [deleteUserSuccess, deleteUserError, dispatch]);

  useEffect(() => {
    if (updateUserSuccess || updateUserError) {
      setTimeout(() => dispatch(clearUpdateUserState()), 3000);
    }
  }, [updateUserSuccess, updateUserError, dispatch]);

  useEffect(() => {
    if (resetPasswordSuccess || resetPasswordError) {
      setTimeout(() => dispatch(clearResetPasswordState()), 3000);
    }
  }, [resetPasswordSuccess, resetPasswordError, dispatch]);

  useEffect(() => {
    if (createUserSuccess || createUserError) {
      setTimeout(() => dispatch(clearCreateUserState()), 3000);
    }
  }, [createUserSuccess, createUserError, dispatch]);

  useEffect(() => {
    if (createUserSuccess && addUserModalOpen) {
      setNotification({
        show: true,
        type: "success",
        message: "User Created Successfully",
        details:
          "The user has been created successfully and is now available in the user list.",
      });
      dispatch(fetchAllUsers() as any);
      setTimeout(() => setAddUserModalOpen(false), 2000);
    }
  }, [createUserSuccess, addUserModalOpen, dispatch]);

  useEffect(() => {
    if (createUserError && addUserModalOpen) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to Create User",
        details:
          createUserError ||
          "An error occurred while creating the user. Please try again.",
      });
      setTimeout(() => setAddUserModalOpen(false), 2000);
    }
  }, [createUserError, addUserModalOpen]);

  useEffect(() => {
    if (updateUserSuccess && addUserModalOpen) {
      setNotification({
        show: true,
        type: "success",
        message: "User Updated Successfully",
        details: "The user has been updated successfully.",
      });
      dispatch(fetchAllUsers() as any);
      setTimeout(() => setAddUserModalOpen(false), 2000);
    }
  }, [updateUserSuccess, addUserModalOpen, dispatch]);

  useEffect(() => {
    if (updateUserError && addUserModalOpen) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to Update User",
        details:
          updateUserError ||
          "An error occurred while updating the user. Please try again.",
      });
      setTimeout(() => setAddUserModalOpen(false), 2000);
    }
  }, [updateUserError, addUserModalOpen]);

  useEffect(() => {
    if (resetPasswordSuccess) {
      setNotification({
        show: true,
        type: "success",
        message: "Password Reset Email Sent",
        details: "A password reset link has been sent to the user.",
      });
    }
  }, [resetPasswordSuccess]);

  useEffect(() => {
    if (resetPasswordError) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to Send Reset Email",
        details:
          resetPasswordError ||
          "An error occurred while sending the password reset email. Please try again.",
      });
    }
  }, [resetPasswordError]);

  useEffect(() => {
    if (deleteUserSuccess) {
      setNotification({
        show: true,
        type: "success",
        message: "User Deleted Successfully",
        details: "The user has been deleted successfully.",
      });
    }
  }, [deleteUserSuccess]);

  useEffect(() => {
    if (deleteUserError) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to Delete User",
        details:
          deleteUserError ||
          "An error occurred while deleting the user. Please try again.",
      });
    }
  }, [deleteUserError]);

  useEffect(() => {
    if (resendWelcomeEmailSuccess) {
      setSendInvitationModalOpen(false);
      dispatch(clearResendWelcomeEmailState());
      setNotification({
        show: true,
        type: "success",
        message: "Invitation Sent Successfully",
        details: "The invitation email has been sent successfully.",
      });
    }
  }, [resendWelcomeEmailSuccess, dispatch]);

  useEffect(() => {
    if (resendWelcomeEmailError) {
      setNotification({
        show: true,
        type: "error",
        message: "Failed to Send Invitation",
        details:
          resendWelcomeEmailError ||
          "An error occurred while sending the invitation. Please try again.",
      });
    }
  }, [resendWelcomeEmailError]);

  const roleOptions = React.useMemo(() => {
    if (!roles.items || roles.items.length === 0) {
      return [];
    }
    return roles.items.map(
      (role: { role_id: string; name: string; label?: string }) => {
        const normalizedRole = normalizeRoleName(role.name);
        const fallbackDisplayName =
          normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
            ? ROLE_DISPLAY_NAMES[
                normalizedRole as keyof typeof ROLE_DISPLAY_NAMES
              ]
            : role.name;
        return {
          label: role.label || fallbackDisplayName,
          value: role.name,
        };
      }
    );
  }, [roles.items]);

  const roleOptionsForAddUser = React.useMemo(
    () =>
      roleOptions.filter(
        (opt: { label: string; value: string }) =>
          normalizeRoleName(opt.value) !== ROLES.EXECUTIVE_SPONSOR &&
          !isAccountTeamRole(opt.value)
      ),
    [roleOptions]
  );

  /** Role filter — Account Team + Executive always available, then other API roles. */
  const roleFilterOptions = React.useMemo(() => {
    const nonAccountTeamRoles = roleOptions.filter(
      (opt: { label: string; value: string }) => !isAccountTeamRole(opt.value)
    );

    const hasExecutive = nonAccountTeamRoles.some(
      (opt: { label: string; value: string }) =>
        normalizeRoleName(opt.value) === ROLES.EXECUTIVE
    );

    const withExecutive = hasExecutive
      ? nonAccountTeamRoles
      : [
          {
            label: ROLE_DISPLAY_NAMES[ROLES.EXECUTIVE],
            value: ROLES.EXECUTIVE,
          },
          ...nonAccountTeamRoles,
        ];

    return [
      { label: "All Roles", value: "" },
      { label: "Account Team", value: ACCOUNT_TEAM_ROLE_FILTER },
      ...withExecutive,
    ];
  }, [roleOptions]);

  const accountTeamRoleOptions = React.useMemo(() => {
    if (!roles.items?.length) {
      return ACCOUNT_TEAM_ROLE_OPTIONS;
    }
    const fromApi = roles.items
      .filter(
        (role: { name: string; role_type?: string }) =>
          role.role_type === "account_team" || isAccountTeamRole(role.name)
      )
      .map((role: { name: string; label?: string }) => ({
        label: role.label || role.name,
        value: role.name,
      }));
    return fromApi.length > 0 ? fromApi : ACCOUNT_TEAM_ROLE_OPTIONS;
  }, [roles.items]);

  const usersWithSchema = users.map(user => {
    const org = organizations.find(o => o.value === user.organization);
    return { ...user, tenant_schema: org?.tenant_schema_name || null };
  });

  const getTenantSchema = (orgName: string): any => {
    const org = organizations.find(o => o.value === orgName);
    return org?.tenant_schema_name || null;
  };

  const filteredUsers = usersWithSchema.filter(user => {
    const matchesSearch =
      (user.user &&
        user.user.toLowerCase().includes(searchValue.toLowerCase())) ||
      (user.email &&
        user.email.toLowerCase().includes(searchValue.toLowerCase()));
    const matchesOrg = !selectedOrg || user.organization === selectedOrg;
    const matchesRole =
      !selectedRole ||
      (selectedRole === ACCOUNT_TEAM_ROLE_FILTER
        ? isAccountTeamRole(user.role)
        : user.role === selectedRole);
    return matchesSearch && matchesOrg && matchesRole;
  });

  const getStatusBadgeClasses = (status: string) => {
    if (status === "active") {
      return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize";
    }
    if (status === "inactive") {
      return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 capitalize";
    }
    return "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 capitalize";
  };

  const handleResetPassword = async () => {
    if (selectedUser?.user_id) {
      await dispatch(
        resetUserPassword({
          tenant_schema: selectedUser.tenant_schema,
          user_id: selectedUser.user_id,
        }) as any
      );
    }
    setResetPasswordModalOpen(false);
  };

  const handleDeleteUser = async () => {
    if (selectedUser?.user_id) {
      await dispatch(
        deleteAdminUser({
          tenant_schema: selectedUser.tenant_schema,
          user_id: selectedUser.user_id,
        }) as any
      );
      dispatch(fetchAllUsers() as any);
    }
    setDeleteUserModalOpen(false);
  };

  const handleToggleUserStatus = async () => {
    if (selectedUser?.user_id) {
      const newStatus =
        selectedUser.status === "active" ? "inactive" : "active";
      await dispatch(
        updateAdminUser({
          tenant_schema: selectedUser.tenant_schema,
          user_id: selectedUser.user_id,
          status: newStatus,
          email: selectedUser.email,
          full_name: selectedUser.user,
          role: selectedUser.role,
        }) as any
      );
      dispatch(fetchAllUsers() as any);
    }
    setToggleStatusModalOpen(false);
  };

  const handleAddOrEditUser = async (data: any) => {
    if (isEditMode && selectedUser?.user_id) {
      await dispatch(
        updateAdminUser({
          tenant_schema: selectedUser.tenant_schema,
          user_id: selectedUser.user_id,
          email: data.email,
          full_name: data.fullName,
          role: data.role,
          status: selectedUser.status,
        }) as any
      );
    } else {
      await dispatch(
        createAdminUser({
          tenant_schema_name: getTenantSchema(data.organization),
          email: data.email,
          full_name: data.fullName,
          role: data.role,
          phone: data.phone,
          position: data.position,
          city: data.city,
          country: data.country,
        }) as any
      );
    }
  };

  const handleSendEmailClick = (user: User) => {
    setSelectedUser(user);
    setSendInvitationModalOpen(true);
  };

  const handleSendInvitationConfirm = () => {
    if (selectedUser?.user_id) {
      dispatch(resendWelcomeEmail(selectedUser.user_id) as any);
    }
  };

  const columns = [
    {
      key: "user",
      title: "USER",
      render: (_: any, record: User) => (
        <div className="flex flex-wrap-cell items-center">
          <div className="bg-gray-100 rounded-lg p-2 mr-3 flex-shrink-0">
            <Users className="h-4 w-4 text-gray-600" />
          </div>
          <div className="cell-info">
            <div className="text-sm font-medium text-gray-900">
              {record.user}
            </div>
            <div className="cell-slug text-xs text-gray-500">
              {record.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "organization",
      title: "Organization",
      render: (_: any, record: User) => (
        <div className="cell-name text-sm text-gray-900">
          {record.organization}
        </div>
      ),
    },
    {
      key: "role",
      title: "ROLE",
      render: (value: string) => {
        const matchedRole = roles.items?.find(
          (role: { name: string; label?: string }) => role.name === value
        );
        const apiRoleLabel = matchedRole?.label;
        const normalizedRole = normalizeRoleName(value);
        const fallbackDisplayName =
          normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
            ? ROLE_DISPLAY_NAMES[
                normalizedRole as keyof typeof ROLE_DISPLAY_NAMES
              ]
            : value;
        return (
          <span className="text-sm text-gray-900">
            {apiRoleLabel || fallbackDisplayName}
          </span>
        );
      },
    },
    {
      key: "status",
      title: "STATUS",
      render: (value: string) => (
        <span className={getStatusBadgeClasses(value)}>{value}</span>
      ),
    },
    {
      key: "last_contacted_at",
      title: "LAST CONTACTED",
      render: (_: unknown, record: User) => (
        <span>
          {record.last_contacted_at
            ? formatDateUS(record.last_contacted_at)
            : "-"}
        </span>
      ),
    },
    {
      key: "last_login_at",
      title: "LAST LOGIN",
      render: (_: unknown, record: User) => (
        <span>
          {record.last_login_at ? formatDateUS(record.last_login_at) : "-"}
        </span>
      ),
    },
    {
      key: "actions",
      title: "ACTIONS",
      render: (_: any, record: User) => (
        <div className="action-icons w-full flex justify-center items-center gap-0">
          <Button
            variant="clear"
            onClick={() => {
              setSelectedUser(record);
              setResetPasswordModalOpen(true);
            }}
            title="Reset Password"
            className="p-1 rounded-md hover:bg-blue-50 transition-all duration-200"
          >
            <Key className="h-4 w-4 text-blue-500" height={18} />
          </Button>
          <Button
            variant="clear"
            onClick={() => handleSendEmailClick(record)}
            title="Send Email"
            className="p-1 rounded-md hover:bg-green-50 transition-all duration-200"
          >
            <Mail className="h-4 w-4 text-green-500" height={18} />
          </Button>
          <Button
            variant="clear"
            onClick={() => {
              setSelectedUser(record);
              setIsEditMode(true);
              setAddUserModalOpen(true);
            }}
            title="Edit"
            className="p-1 rounded-md hover:bg-orange-50 transition-all duration-200"
          >
            <Pencil className="h-4 w-4 text-orange-500" height={18} />
          </Button>
          <Button
            variant="clear"
            onClick={() => {
              setSelectedUser(record);
              setToggleStatusModalOpen(true);
            }}
            title={
              record.status === "active" ? "Deactivate User" : "Activate User"
            }
            className="p-1 rounded-md hover:bg-orange-50 transition-all duration-200"
          >
            {record.status === "active" ? (
              <Lock className="h-4 w-4 text-orange-600" height={18} />
            ) : (
              <Unlock className="h-4 w-4 text-orange-600" height={18} />
            )}
          </Button>
          <Button
            variant="clear"
            onClick={() => {
              setSelectedUser(record);
              setDeleteUserModalOpen(true);
            }}
            title="Delete"
            className="p-1 rounded-md hover:bg-red-50 transition-all duration-200"
          >
            <Trash2 className="h-4 w-4 text-red-500" height={18} />
          </Button>
        </div>
      ),
      className: "flex justify-center",
    },
  ];

  return (
    <>
      <AdminTableContainer
        columns={columns}
        data={filteredUsers}
        loading={loading}
        showSearch
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search users..."
        showAddButton
        addButtonLabel="Add User"
        addButtonIcon={<Plus size={16} />}
        onAddClick={() => {
          setIsEditMode(false);
          setSelectedUser(null);
          setAddUserModalOpen(true);
        }}
        showFilters
        filters={[
          {
            label: "Organization",
            selected: selectedOrg,
            options: [
              { label: "All Organizations", value: "" },
              ...organizations,
            ],
            onSelect: setSelectedOrg,
          },
          {
            label: "Role",
            selected: selectedRole,
            options: roleFilterOptions,
            onSelect: setSelectedRole,
          },
        ]}
        title="User Management"
        // maxHeight="calc(100vh - 25.5rem)"
        pagination={true}
      />
      <AddUserModal
        isOpen={addUserModalOpen}
        roleOptions={roleOptionsForAddUser}
        accountTeamRoleOptions={accountTeamRoleOptions}
        toggle={() => setAddUserModalOpen(false)}
        organizations={organizations}
        loading={isEditMode ? updateUserLoading : createUserLoading}
        onSubmit={handleAddOrEditUser}
        editData={
          isEditMode && selectedUser
            ? {
                organization: selectedUser.organization || "",
                tenant_schema: selectedUser.tenant_schema,
                fullName: selectedUser.user,
                email: selectedUser.email,
                role: selectedUser.role,
              }
            : null
        }
        isEditMode={isEditMode}
      />
      <SuccessNotification
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        message={notification.message}
        details={notification.details}
        type={notification.type}
      />

      {resetPasswordModalOpen && (
        <ResetPasswordModal
          isOpen={resetPasswordModalOpen}
          toggle={() => setResetPasswordModalOpen(false)}
          userName={selectedUser?.user || ""}
          onSubmit={handleResetPassword}
          loading={resetPasswordLoading}
        />
      )}
      <DeleteModal
        isOpen={deleteUserModalOpen}
        toggle={() => setDeleteUserModalOpen(false)}
        title="Delete User"
        entityName={selectedUser?.user || ""}
        entityDetails={selectedUser?.email || ""}
        warningItems={["User account and profile", "All associated data"]}
        onConfirm={handleDeleteUser}
        loading={deleteUserLoading}
      />

      <ToggleUserStatusModal
        isOpen={toggleStatusModalOpen}
        toggle={() => setToggleStatusModalOpen(false)}
        userName={selectedUser?.user || ""}
        currentStatus={selectedUser?.status || ""}
        onConfirm={handleToggleUserStatus}
        loading={updateUserLoading}
      />
      <SendInvitationModal
        isOpen={sendInvitationModalOpen}
        toggle={() => setSendInvitationModalOpen(false)}
        userName={selectedUser?.user || ""}
        userEmail={selectedUser?.email || ""}
        onConfirm={handleSendInvitationConfirm}
        loading={resendWelcomeEmailLoading}
      />
    </>
  );
};

export default AdminUserManagementContainer;
